import { config } from "../config";
import { normalizeIndianMobile } from "./login";
import { humanRefused, withHuman } from "../auth/human";

/**
 * Claiming a sponsor invitation: POST /access/sponsor/claim (radaccess v1.67).
 *
 * ⛔ PUBLIC. No identity is read and no token is sent. The widget proves the
 * mobile in the browser; radaccess confirms that proof with MSG91 FIRST, then
 * asks radcustomer whether the link is still pending for that mobile, then
 * creates the `sponsor` login in the organisation's namespace -- or, when the
 * mobile already has a login in that namespace, adds `sponsor` to it and links
 * it (design §13.4).
 *
 * 🔑 One answer for every bad link. Unknown, cancelled, claimed, expired and
 * wrong-mobile all come back as the same 404, and this page says the same
 * thing for all of them -- it must not become a way to test links.
 */

export interface ClaimRequest {
  /** The link's secret, exactly as the operator sent it. */
  invite: string;
  mobile: string;
  firstName: string;
  lastName: string;
  password: string;
  /** The access token MSG91's widget returned for this mobile. */
  widgetToken: string;
}

export type ClaimOutcome =
  /** 201: a new login. Sign in with the mobile and the password just chosen. */
  | { kind: "created"; organisation: string; note: string }
  /** 200: the mobile's existing login was linked. Sign in with its password. */
  | { kind: "linked"; organisation: string; note: string }
  /** 400/422: the form was refused, with reasons. */
  | { kind: "incomplete"; problems: string[] }
  /** 401: the widget token did not prove this mobile, or was already used. */
  | { kind: "not_verified" }
  /** 404: the link is not valid -- one answer for every reason. */
  | { kind: "bad_link" }
  /** 409: the mobile's login cannot take this invitation; the message says why. */
  | { kind: "conflict"; message: string }
  /** 429: too many attempts, or MSG91 is throttling. */
  | { kind: "busy"; retryAfter: number }
  /** 502: something failed part-way; the same link may be tried again. */
  | { kind: "retry"; message: string }
  /** 503, 5xx or no answer: claiming is not available. */
  | { kind: "unavailable"; status: number | null };

const text = (v: unknown): string => (typeof v === "string" ? v : "");

/** The invitation secret from the page's query string, or "" when absent. */
export function inviteFromSearch(search: string): string {
  return (new URLSearchParams(search).get("invite") ?? "").trim();
}

/**
 * Whether claiming can work at all right now, asked as the page opens and
 * BEFORE any SMS code is sent.
 *
 * On 2026-09-19 the claim route was not configured: a person typed their
 * details, received and entered an SMS code, and only then read "Accepting
 * invitations is not available right now". The code was spent for nothing.
 *
 * 🔑 HOW, AND WHY IT IS SAFE. radaccess answers 503 for an unconfigured route
 * before it reads the body, and a configured one refuses an empty body with
 * 422 before it counts an attempt, calls MSG91 or looks at any invitation
 * (radaccess app/handler/sponsorclaim.go, ServeHTTP). So an empty POST tells
 * us exactly "can this route work", and nothing else: no link, no mobile, no
 * invitation is sent, so it cannot be used to learn anything about one.
 *
 * ⚠️ It deliberately does NOT check the link. radaccess looks up an
 * invitation only after the SMS code proves the mobile, so that a leaked link
 * cannot be used to test which number it was for. A "check the link first"
 * would undo that.
 *
 * Anything other than a clear 503 or 422 is "unknown": the page lets the
 * person try, rather than blocking them on a probe it could not read.
 */
export type ClaimAvailability = "available" | "unavailable" | "unknown";

export async function claimAvailability(signal?: AbortSignal): Promise<ClaimAvailability> {
  let res: Response;
  try {
    res = await fetch(`${config().gatewayBase}/sponsor/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: "{}",
      ...(signal ? { signal } : {}),
    });
  } catch {
    return "unknown";
  }
  if (res.status === 503) return "unavailable";
  if (res.status === 422) return "available";
  // With the human check on, {} is refused for the missing token first.
  if (res.status === 400 && (await res.json().catch(() => ({})) as { error?: string }).error === "captcha_required") return "available";
  return "unknown";
}

export async function claimInvite(input: ClaimRequest, signal?: AbortSignal): Promise<ClaimOutcome> {
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return { kind: "incomplete", problems: ["Enter a 10-digit Indian mobile number."] };
  const invite = input.invite.trim();
  if (!invite) return { kind: "bad_link" };
  const widgettoken = input.widgetToken.trim();
  if (!widgettoken) return { kind: "not_verified" };

  let res: Response;
  try {
    res = await fetch(`${config().gatewayBase}/sponsor/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(withHuman("/sponsor/claim", {
        invite,
        mobile,
        widgettoken,
        password: input.password,
        firstname: input.firstName.trim(),
        lastname: input.lastName.trim(),
      })),
      ...(signal ? { signal } : {}),
    });
  } catch {
    return { kind: "unavailable", status: null };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  humanRefused("/sponsor/claim", res.status, body);

  switch (res.status) {
    case 201:
      return { kind: "created", organisation: text(body.organisation_name), note: text(body.note) };
    case 200:
      return { kind: "linked", organisation: text(body.organisation_name), note: text(body.note) };
    case 400:
    case 422: {
      const listed = Array.isArray(body.problems) ? body.problems.filter((x): x is string => typeof x === "string") : [];
      return { kind: "incomplete", problems: listed.length > 0 ? listed : [text(body.error) || "The details are incomplete."] };
    }
    case 401:
      return { kind: "not_verified" };
    case 404:
      return { kind: "bad_link" };
    case 409:
      return { kind: "conflict", message: text(body.error) || "This mobile number cannot accept this invitation." };
    case 429: {
      // Retry-After is not CORS-safelisted, so a browser may hide it.
      const after = Number(res.headers.get("Retry-After"));
      return { kind: "busy", retryAfter: Number.isFinite(after) && after > 0 ? after : 60 };
    }
    case 502:
      return { kind: "retry", message: text(body.error) || "Something failed part-way. Try the link again in a minute." };
    default:
      return { kind: "unavailable", status: res.status };
  }
}
